import { WIZARD_STEP_CARD_POINTER_CARD_OVERLAP_PX } from './constants';
import type { WizardPlacement, WizardPointerAnchor } from './types';

/** UserMenu-style flat pointer (long edge on bottom of path; tip at top-right). */
export const CORNER_POINTER_WIDTH = 38;
export const CORNER_POINTER_HEIGHT = 30;

export const CORNER_POINTER_EDGE_NUDGE_Y = 4;

/**
 * Pointer join on card edge → tip (px). Matches `bottom-top-right` CSS transform.
 */
export const CORNER_POINTER_STEM_TO_TIP_PX =
  CORNER_POINTER_HEIGHT -
  WIZARD_STEP_CARD_POINTER_CARD_OVERLAP_PX +
  CORNER_POINTER_EDGE_NUDGE_Y;

/** Layout gap from card edge to align target when tip points down (`top-bottom-right`). */
export const CORNER_POINTER_TARGET_GAP_DOWN_PX =
  CORNER_POINTER_HEIGHT + CORNER_POINTER_HEIGHT / 2;

/** Card edge → align target for corner pointers (layout only). */
export function getCornerPointerTargetGapPx(
  variant: CornerPointerVariant,
): number {
  return variant === 'top-bottom-right'
    ? CORNER_POINTER_TARGET_GAP_DOWN_PX
    : CORNER_POINTER_STEM_TO_TIP_PX;
}

export type CornerPointerVariant =
  | 'right-top-left'
  | 'top-bottom-right'
  | 'bottom-top-right';

export const CORNER_POINTER_CLASS: Record<CornerPointerVariant, string> = {
  'right-top-left': 'wizard-step-card-pointer--corner-right-top-left',
  'top-bottom-right': 'wizard-step-card-pointer--corner-top-bottom-right',
  'bottom-top-right': 'wizard-step-card-pointer--corner-bottom-top-right',
};

export function getCornerPointerVariant(
  placement: WizardPlacement,
  pointerAnchor?: WizardPointerAnchor,
): CornerPointerVariant | null {
  if (pointerAnchor === 'top-left' && placement === 'right') {
    return 'right-top-left';
  }
  if (pointerAnchor === 'bottom-right' && placement === 'top') {
    return 'top-bottom-right';
  }
  if (pointerAnchor === 'top-right' && placement === 'bottom') {
    return 'bottom-top-right';
  }
  return null;
}

export function getCornerPointerPath(width: number, height: number): string {
  return `M0 ${height}C11 ${height} 29 ${height - 4} ${width} 0V${height}H0Z`;
}

export function getCornerPointerSvgTransform(
  variant: CornerPointerVariant,
  width: number,
  height: number,
): string | undefined {
  if (variant !== 'right-top-left') {
    return undefined;
  }
  return `rotate(-90 ${width / 2} ${height / 2})`;
}

/** Per-variant layout tuning for corner pointers. */
export type CornerPointerLayout = {
  tipInsetFromCorner: number;
};

export const CORNER_POINTER_LAYOUT: Record<CornerPointerVariant, CornerPointerLayout> =
  {
    'top-bottom-right': {
      tipInsetFromCorner: 2,
    },
    'bottom-top-right': {
      tipInsetFromCorner: 0,
    },
    'right-top-left': {
      tipInsetFromCorner: 0,
    },
  };

export function getCornerPointerStyleVars(): Record<string, string> {
  return {
    '--wizard-corner-pointer-height': `${CORNER_POINTER_HEIGHT}px`,
    '--wizard-corner-edge-nudge-y': `${CORNER_POINTER_EDGE_NUDGE_Y}px`,
    '--wizard-step-card-pointer-overlap': `${WIZARD_STEP_CARD_POINTER_CARD_OVERLAP_PX}px`,
  };
}

/** Side span for `right-top-left` (−90° path rotation). */
export const RIGHT_TOP_LEFT_SIDE_SPAN_PX =
  CORNER_POINTER_WIDTH -
  WIZARD_STEP_CARD_POINTER_CARD_OVERLAP_PX -
  4;

/** Vertical offset from card top to pointer tip (`right-top-left`). */
export const RIGHT_TOP_LEFT_TIP_FROM_CARD_TOP_PX =
  CORNER_POINTER_EDGE_NUDGE_Y - 4;
